import { Component, createRef } from "react";
import { winampBarLevels, SPECTRUM_BARS, SPECTRUM_LEVELS } from "../spectrum";

// Winamp's peak markers hang for a moment, then slide down a pixel at a time.
const PEAK_HOLD_MS = 400;
const PEAK_FALL_PER_SECOND = 14;
// Winamp's ticker separates the wrap-around with asterisks and baked-in
// spacing, and the separator travels with the text — which is also what makes
// a two-copy strip loop seamlessly, since one copy is then exactly half the
// strip.
const MARQUEE_SEPARATOR = "  ***  ";
// It scrolls at a steady rate rather than a fixed duration, so a long title
// does not race past while a short one crawls.
const MARQUEE_PX_PER_SECOND = 34;

/**
 * The Winamp 2 display: a scrolling track title over a 19-bar analyser.
 *
 * The bars are generated here from playback state rather than from real audio.
 * Reading the tab's AnalyserNode would mean a message per frame from the
 * content script for as long as the popup is open; the movement reads the same
 * at this size, and the popup stays silent. Frames are written straight to the
 * DOM through refs — re-rendering 19 nodes at 60fps through React would be the
 * expensive part of this component.
 */
export default class WinampVisualizer extends Component {
  constructor(props) {
    super(props);
    this.barRefs = Array.from({ length: SPECTRUM_BARS }, () => createRef());
    this.peakRefs = Array.from({ length: SPECTRUM_BARS }, () => createRef());
    this.peaks = new Array(SPECTRUM_BARS).fill(0);
    this.peakHeldAt = new Array(SPECTRUM_BARS).fill(0);
    this.frame = null;
    this.lastAt = 0;
    this.wellRef = createRef();
    this.stripRef = createRef();
    this.copyRef = createRef();
    // Copies of the ticker in the strip; always even, so translating half the
    // strip lands the next copy exactly where the first began.
    this.state = { copies: 2 };
  }

  componentDidMount() {
    this.reducedMotion =
      globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ===
      true;
    this.measureTitle();
    this.start();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.trackName !== this.props.trackName) this.measureTitle();
    this.start();
  }

  // The ticker runs whatever the title's length, so a short name has to be
  // repeated enough times to fill the well — otherwise the strip is narrower
  // than the display and the loop shows a bare gap. Half the strip must cover
  // the well, and the two halves must be identical for the wrap to be
  // seamless, so the count is always even.
  //
  // Measuring in pixels also gives the travel distance, which sets the
  // duration for a constant scroll rate: the same characters come out a
  // different width in every title.
  measureTitle() {
    const well = this.wellRef.current;
    const copy = this.copyRef.current;
    const strip = this.stripRef.current;
    if (!well || !copy || !strip) return;

    const copyWidth = copy.getBoundingClientRect().width;
    // Fonts may not have landed yet, and a zero width would ask for an
    // infinite number of copies.
    if (copyWidth < 1) return;

    const half = Math.max(1, Math.ceil(well.clientWidth / copyWidth));
    const travel = half * copyWidth;
    strip.style.animationDuration = `${Math.max(3, travel / MARQUEE_PX_PER_SECOND)}s`;

    const copies = half * 2;
    if (copies !== this.state.copies) this.setState({ copies });
  }

  componentWillUnmount() {
    this.stop();
  }

  // Nothing playing means nothing to draw: the loop stops rather than
  // spending a frame a tick writing zeros behind a paused video.
  start() {
    if (!this.props.playing) {
      this.stop();
      this.rest();
      return;
    }
    if (this.frame != null) return;
    if (!globalThis.requestAnimationFrame) return;
    this.lastAt = 0;
    this.frame = globalThis.requestAnimationFrame((at) => this.tick(at));
  }

  // One last frame so the bars settle empty instead of freezing wherever the
  // music stopped.
  rest() {
    this.peaks.fill(0);
    for (let bar = 0; bar < this.barRefs.length; bar += 1) {
      const fill = this.barRefs[bar].current;
      if (fill) fill.style.height = "0%";
      const peak = this.peakRefs[bar].current;
      if (peak) peak.style.bottom = "0%";
    }
  }

  stop() {
    if (this.frame == null) return;
    globalThis.cancelAnimationFrame?.(this.frame);
    this.frame = null;
  }

  tick(at) {
    const elapsed = this.lastAt ? Math.min(0.1, (at - this.lastAt) / 1000) : 0;
    this.lastAt = at;

    const { playing = false, slowControl = 1 } = this.props;
    const levels = winampBarLevels({
      time: at / 1000,
      playing,
      slowControl,
      reducedMotion: this.reducedMotion,
    });

    for (let bar = 0; bar < levels.length; bar += 1) {
      const level = levels[bar];
      const fill = this.barRefs[bar].current;
      if (fill) fill.style.height = `${(level / SPECTRUM_LEVELS) * 100}%`;

      if (level >= this.peaks[bar]) {
        this.peaks[bar] = level;
        this.peakHeldAt[bar] = at;
      } else if (at - this.peakHeldAt[bar] > PEAK_HOLD_MS) {
        this.peaks[bar] = Math.max(
          level,
          this.peaks[bar] - PEAK_FALL_PER_SECOND * elapsed,
        );
      }

      const peak = this.peakRefs[bar].current;
      if (peak) {
        peak.style.bottom = `${Math.min(100, (this.peaks[bar] / SPECTRUM_LEVELS) * 100)}%`;
      }
    }

    this.frame = globalThis.requestAnimationFrame((next) => this.tick(next));
  }

  render() {
    const { trackName = "", playing = false } = this.props;
    const title = trackName || (playing ? "Playing" : "Slowed");
    const { copies } = this.state;
    // The separator rides with the text, so the next copy starts exactly one
    // strip-half later and the wrap has no seam.
    const ticker = `${title}${MARQUEE_SEPARATOR}`;
    // Winamp keeps the name moving while it plays, however short it is; it
    // settles only when the music stops.
    const scrolls = playing;

    return (
      <div className="winamp-vis" aria-hidden="true">
        <div className="winamp-vis__display">
          <div className="winamp-vis__title" ref={this.wellRef}>
            {/* The strip is taken out of flow on purpose. A long track name is
                far wider than the well, and an in-flow strip contributes that
                width to the header, up through the shrink-to-fit popup
                document, until Chrome sizes the window to its 800px cap and
                leaves a blank strip beside the layout. Absolutely positioned,
                it contributes nothing. */}
            <div
              ref={this.stripRef}
              className={`winamp-vis__strip${scrolls ? " winamp-vis__strip--marquee" : ""}`}
            >
              {Array.from({ length: scrolls ? copies : 1 }, (_, index) => (
                <span
                  key={index}
                  className="winamp-vis__title-text"
                  ref={index === 0 ? this.copyRef : undefined}
                >
                  {ticker}
                </span>
              ))}
            </div>
          </div>

          <div className="winamp-vis__bars">
            {this.barRefs.map((ref, index) => (
              <div className="winamp-vis__bar" key={index}>
                <div className="winamp-vis__bar-fill" ref={ref} />
                <div
                  className="winamp-vis__bar-peak"
                  ref={this.peakRefs[index]}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
}
